import { Component, Input, Output, EventEmitter, inject, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService, AccessLevelGrant } from '../../../../core/services/settings.service';
import { MODULE } from '../../../../core/services/permission.service';
import { UserTypeBadgeService } from '../../../../core/services/user-type-badge.service';
import { accessModuleKey } from '../../../../core/utils/access-module.utils';
import { firstValueFrom, Subscription } from 'rxjs';
import { toast } from 'ngx-sonner';

import { Dialog } from 'primeng/dialog';
import { MultiSelect } from 'primeng/multiselect';
import { InputText } from 'primeng/inputtext';
import { Popover } from 'primeng/popover';
import { AppButton } from '../../../../shared/components/app-button/app-button';

type PermissionAction = 'can_view' | 'can_create' | 'can_edit' | 'can_delete';
type AccessLevelFlags = Record<PermissionAction, boolean>;
/** The boxes a module draws. A module with a parent renders indented under it. */
type ModuleShape = { actions: PermissionAction[]; parent?: string; labelKey?: string };

@Component({
  selector: 'app-profile-settings-modal',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule,
    FormsModule,
    TranslateModule, 
    Dialog,
    MultiSelect,
    InputText,
    Popover,
    AppButton
  ],
  templateUrl: './profile-settings-modal.html',
})
export class ProfileSettingsModal implements OnInit, OnDestroy {
  @Input() profileId?: number;
  @Input() branchId!: number | null;
  @Input() readOnly = false;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private settingsService = inject(SettingsService);
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  userTypeBadge = inject(UserTypeBadgeService);

  profileForm!: FormGroup;
  isEditMode = false;
  saving = false;
  showError = false;
  private langChangeSub?: Subscription;

  /**
   * Escape closes the HINT and nothing else. PrimeNG's popover closes on Escape without stopping
   * the event, and the dialog around it closes on the same keypress - so putting a hint away
   * discarded every unticked box with it. Captured on the document and stopped dead while a hint
   * is open; with none open the key reaches the dialog as before.
   */
  private readonly swallowHintEscape = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !this.hintFor) return;
    // Immediate: the popover's and the dialog's handlers are both on the document too.
    event.stopImmediatePropagation();
    this.closeHint();
    this.cdr.detectChanges();
  };

  ngOnDestroy() {
    this.langChangeSub?.unsubscribe();
    document.removeEventListener('keydown', this.swallowHintEscape, true);
  }
  
  // Users
  availableUsers: any[] = [];
  selectedUsers: any[] = [];
  loadingUsers = false;
  showUsersDropdown = false;

  // Permissions
  availablePermissions: any[] = [];
  // Map keyed by module (access_level) id -> its granted CRUD flags.
  selectedPermissions: Record<number, AccessLevelFlags> = {};
  permissionSearch = '';
  readonly permissionActions: { key: PermissionAction; icon: string; labelKey: string }[] = [
    { key: 'can_view', icon: 'pi-eye', labelKey: 'permission_action_view' },
    { key: 'can_create', icon: 'pi-plus', labelKey: 'permission_action_create' },
    { key: 'can_edit', icon: 'pi-pencil', labelKey: 'permission_action_edit' },
    { key: 'can_delete', icon: 'pi-trash', labelKey: 'permission_action_delete' },
  ];

  /** One-line scope hint shown under a module's name, for modules whose exact
   * boundary is easy to get wrong when assigning a role. */
  private readonly moduleHintKeys: Record<string, string> = {
    [MODULE.CONSULTAS]: 'permission_hint_consultas',
    [MODULE.CONSULTAS_ADMIN]: 'permission_hint_consultas_admin',
    [MODULE.FEEDBACK]: 'permission_hint_feedback',
    [MODULE.FEEDBACK_ALL]: 'permission_hint_feedback_all',
    [MODULE.FEEDBACK_NOTES]: 'permission_hint_feedback_notes',
    [MODULE.COMPLAINTS]: 'permission_hint_complaints',
  };

  moduleHintKey(permission: any): string | null {
    return this.moduleHintKeys[permission.name] ?? null;
  }

  /** The hint's bold first line: the module's full name, which for a child row carries its parent. */
  hintTitleKey(permission: any): string {
    return accessModuleKey(permission.name);
  }

  /** Which actions a module really has, and whether it is a child of another module.
   * Modules absent here keep all four boxes. Display only: nothing here changes what is
   * stored or how any check site reads a grant. */
  private readonly moduleShapes: Record<string, ModuleShape> = {
    [MODULE.FEEDBACK]: { actions: ['can_view', 'can_create'] },
    [MODULE.FEEDBACK_ALL]: {
      actions: ['can_view'], parent: MODULE.FEEDBACK, labelKey: 'permission_child_feedback_all',
    },
    [MODULE.FEEDBACK_NOTES]: {
      actions: ['can_view'], parent: MODULE.FEEDBACK, labelKey: 'permission_child_feedback_notes',
    },
    [MODULE.COMPLAINTS]: { actions: ['can_view', 'can_create', 'can_edit'] },
  };

  hasAction(permission: any, action: PermissionAction): boolean {
    const shape = this.moduleShapes[permission.name];
    return !shape || shape.actions.includes(action);
  }

  isChild(permission: any): boolean {
    return !!this.moduleShapes[permission.name]?.parent;
  }

  /** The i18n key for the row label: a child is written as an action, every other module
   * keeps its module name. */
  rowLabelKey(permission: any): string {
    return this.moduleShapes[permission.name]?.labelKey ?? accessModuleKey(permission.name);
  }

  /** A child's box is inert while its parent has no View. The stored value is kept, so a
   * grant made before the parent was unticked comes back when it is ticked again. */
  isChildLocked(permission: any): boolean {
    const parentName = this.moduleShapes[permission.name]?.parent;
    if (!parentName) return false;
    const parent = this.availablePermissions.find((p) => p.name === parentName);
    return !parent || !this.isActionEnabled(parent, 'can_view');
  }

  /** A cell that cannot be clicked: absent from the module's shape, locked by the parent,
   * or implied by Consultas. */
  isCellInert(permission: any, action: PermissionAction): boolean {
    return (
      !this.hasAction(permission, action) ||
      this.isChildLocked(permission) ||
      this.isImpliedByConsultas(permission, action)
    );
  }

  /** The module whose hint the shared panel holds, or null when nothing is open. The panel is
   * out of flow, so opening one never changes the dialog's height - which is what broke under an
   * active search, where the matrix is short enough that an inline hint moved its own icon away
   * from the pointer. One panel serves every module. */
  hintFor: any | null = null;

  @ViewChild('hintPanel') private hintPanel?: Popover;

  /** The anchor a click asked for while the panel was still closing. Moving the hint to another
   * icon has to go through a full hide: PrimeNG re-anchors only on a fresh show, and its own
   * `toggle` re-shows from inside `onAnimationEnd`, which then sets `render = false` and swallows
   * it. So the re-show waits for the hide to finish. */
  private pendingHint: { permission: any; anchor: HTMLElement } | null = null;

  /**
   * Click is the only way in. Hover opened a panel whose ~120ms leave animation raced every
   * other gesture, and under zoneless change detection a sweep between two icons resolved
   * before any render - the panel then showed one module's text at another's coordinates.
   * Clicking the open module closes it; clicking another moves the panel there.
   */
  toggleHint(event: Event, permission: any) {
    // Kept off the document, or PrimeNG's own outside-click listener closes the panel this very
    // click is opening.
    event.stopPropagation();
    const panel = this.hintPanel;
    if (!panel) return;
    if (this.hintFor?.id === permission.id) {
      this.closeHint();
      return;
    }
    // `render`, not `overlayVisible`: `hide()` clears that flag while the element is still
    // mounted, and a show against a mounted panel never re-anchors.
    if (panel.render) {
      this.pendingHint = { permission, anchor: event.currentTarget as HTMLElement };
      panel.hide();
      return;
    }
    this.pendingHint = null;
    this.hintFor = permission;
    this.hintAnchor = event.currentTarget as HTMLElement;
    panel.show(undefined, this.hintAnchor);
  }

  /** The icon the open hint belongs to, for the flip below. */
  private hintAnchor: HTMLElement | null = null;

  /** Opens the hint UPWARD when it would otherwise run under the dialog's footer, where the save
   * and cancel buttons sit. The library only flips at the viewport's edge. */
  onHintShown() {
    const container = this.hintPanel?.container;
    const anchor = this.hintAnchor;
    const footer = anchor?.closest('.p-dialog')?.querySelector('.p-dialog-footer');
    if (!container || !anchor || !footer) return;
    const panel = container.getBoundingClientRect();
    if (panel.bottom <= footer.getBoundingClientRect().top) return;
    // With the flipped class the library's gutter moves above the anchor instead of below it.
    container.style.top = `${anchor.getBoundingClientRect().top + window.scrollY - panel.height}px`;
    container.classList.add('p-popover-flipped');
  }

  /** Cancels a queued move as well as closing: on an already-hiding panel `hide()` does nothing,
   * and the move would then reopen the hint this call was meant to put away. */
  closeHint() {
    this.pendingHint = null;
    this.hintPanel?.hide();
  }

  /** The single place the hint's state is cleared, so the panel's own exits - Escape, a click
   * outside, the scroll listener - leave nothing behind, including the icon's active colour.
   * An `onHide` that belongs to a SUPERSEDED anchor clears nothing: the click that replaced it
   * has already named the next one, and the content stays put until the panel is really gone. */
  onHintHidden() {
    const next = this.pendingHint;
    this.pendingHint = null;
    if (!next) {
      this.hintFor = null;
      return;
    }
    // After this handler returns, so the library's own `render = false` cannot swallow the show.
    queueMicrotask(() => {
      // The search box can filter the queued anchor's row away while the panel is still closing;
      // anchoring to a detached element positions the panel from a zeroed rect, at the page corner.
      if (!next.anchor.isConnected) {
        this.hintFor = null;
        return;
      }
      this.hintFor = next.permission;
      this.hintAnchor = next.anchor;
      this.hintPanel?.show(undefined, next.anchor);
    });
  }

  loading = false;

  async ngOnInit() {
    this.isEditMode = !!this.profileId;
    this.initForm();
    // Capture phase, so it runs before the popover's and the dialog's own document handlers.
    document.addEventListener('keydown', this.swallowHintEscape, true);

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.stampModuleSearchLabels();
      this.cdr.detectChanges();
    });
    
    this.loading = true;
    
    // Load permissions and users in parallel
    await Promise.all([
      this.loadPermissions(),
      this.loadUsers()
    ]);
    
    // Load profile data after users are loaded (for edit mode)
    if (this.isEditMode && this.profileId) {
      await this.loadProfileData();
    }
    
    this.loading = false;
    this.cdr.detectChanges();
  }

  initForm() {
    this.profileForm = this.fb.group({
      name: ['', Validators.required]
    });
  }

  async loadUsers() {
    if (!this.branchId) return;
    this.loadingUsers = true;
    try {
      // Use getAvailableUsersForProfile which calls get_users_available_or_in_profile
      // Pass null for new profiles, or profileId for editing
      this.availableUsers = await firstValueFrom(
        this.settingsService.getAvailableUsersForProfile(this.profileId || null, this.branchId)
      );
    } catch (err) {
      console.error(err);
    } finally {
      this.loadingUsers = false;
      this.cdr.detectChanges();
    }
  }

  async loadPermissions() {
    try {
      const levels = await firstValueFrom(this.settingsService.getAccessLevels());
      this.availablePermissions = this.groupChildrenUnderParents(
        this.groupConsultasAdministrativo(levels),
      );
      this.stampModuleSearchLabels();
      this.cdr.detectChanges();
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Precomputes the lowercased translated label per module for the search
   * filter, so no translate lookup runs inside the getter (which is hit
   * several times per change-detection pass). Guarded: a null/blank module
   * name or a key resolving to a non-string never reaches instant()/throws.
   */
  private stampModuleSearchLabels() {
    for (const p of this.availablePermissions) {
      // A child also matches its parent's name, so a search for the module keeps the sub-rows.
      const parentName = this.moduleShapes[p.name]?.parent;
      const keys = [this.rowLabelKey(p), parentName ? accessModuleKey(parentName) : ''];
      p.search_label = keys
        .filter(Boolean)
        .map((k) => String(this.translate.instant(k)).toLowerCase())
        .join(' ');
    }
  }

  /** Keep "Consultas - Administrativo" directly under "Consultas" in the matrix —
   * they're a paired concept (see isImpliedByConsultas) and should read as one. */
  private groupConsultasAdministrativo(levels: any[]): any[] {
    const adminIndex = levels.findIndex((l) => l.name === MODULE.CONSULTAS_ADMIN);
    const consultasIndex = levels.findIndex((l) => l.name === MODULE.CONSULTAS);
    if (adminIndex === -1 || consultasIndex === -1 || adminIndex === consultasIndex + 1) {
      return levels;
    }
    const reordered = [...levels];
    const [admin] = reordered.splice(adminIndex, 1);
    const newConsultasIndex = reordered.findIndex((l) => l.name === MODULE.CONSULTAS);
    reordered.splice(newConsultasIndex + 1, 0, admin);
    return reordered;
  }

  /** Every module with a parent in the shape map moves to sit directly under that parent,
   * in their original relative order. Consultas keeps its own grouping above. */
  private groupChildrenUnderParents(levels: any[]): any[] {
    const children = levels.filter((l) => this.moduleShapes[l.name]?.parent);
    if (!children.length) return levels;
    const rest = levels.filter((l) => !this.moduleShapes[l.name]?.parent);
    const out: any[] = [];
    for (const l of rest) {
      out.push(l);
      out.push(...children.filter((c) => this.moduleShapes[c.name]!.parent === l.name));
    }
    // A child whose parent is not in the list keeps its own row at the end.
    out.push(...children.filter((c) => !rest.some((l) => l.name === this.moduleShapes[c.name]!.parent)));
    return out;
  }

  async loadProfileData() {
    if (!this.profileId || !this.branchId) return;
    try {
      const profile = await firstValueFrom(
        this.settingsService.getProfileDetails(this.profileId, this.branchId)
      );
      
      if (profile) {
        // Set profile name
        this.profileForm.patchValue({ name: profile.name });
        
        // Set selected permissions from access_levels (array of {level_id, can_*}).
        this.selectedPermissions = {};
        for (const grant of (profile.access_levels || [])) {
          this.selectedPermissions[grant.level_id] = {
            can_view: !!grant.can_view,
            can_create: !!grant.can_create,
            can_edit: !!grant.can_edit,
            can_delete: !!grant.can_delete,
          };
        }
        
        // Set selected users - filter available users by user_ids from profile
        if (profile.user_ids && profile.user_ids.length > 0) {
          this.selectedUsers = this.availableUsers.filter(u => 
            profile.user_ids.includes(u.user_id)
          );
        }
        
        this.cdr.detectChanges();
      }
    } catch (err) {
      console.error('Error loading profile data:', err);
      toast.error(this.translate.instant('error_loading_data'));
    }
  }

  toggleUsersDropdown() {
    this.showUsersDropdown = !this.showUsersDropdown;
  }

  toggleUser(user: any) {
    const index = this.selectedUsers.findIndex(u => u.user_id === user.user_id);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(user);
    }
  }

  isUserSelected(user: any): boolean {
    return this.selectedUsers.some(u => u.user_id === user.user_id);
  }

  // ---- Permissions matrix -------------------------------------------------
  // A module is "granted" when it has at least one CRUD action enabled; its
  // flags object then lives in `selectedPermissions` keyed by the module id.

  private flagsFor(permission: any, create = false): AccessLevelFlags | undefined {
    let flags = this.selectedPermissions[permission.id];
    if (!flags && create) {
      flags = { can_view: false, can_create: false, can_edit: false, can_delete: false };
      this.selectedPermissions[permission.id] = flags;
    }
    return flags;
  }

  /** Remove the module from the map when none of its DRAWN actions is enabled; a flag on an
   * action the module does not draw cannot keep the row alive. */
  private pruneIfEmpty(permission: any) {
    const flags = this.selectedPermissions[permission.id];
    const drawn = this.permissionActions.filter((a) => this.hasAction(permission, a.key));
    if (flags && drawn.every((a) => !flags[a.key])) {
      delete this.selectedPermissions[permission.id];
    }
  }

  /** A module counts as granted when any action is enabled (including one implied
   * onto "Consultas - Administrativo" by "Consultas", even before it's ever been
   * explicitly touched). */
  isPermissionSelected(permission: any): boolean {
    return (
      !!this.selectedPermissions[permission.id] ||
      this.permissionActions.some((a) => this.isImpliedByConsultas(permission, a.key))
    );
  }

  /**
   * "Consultas - Administrativo" write access is auto-implied by "Consultas" write
   * access (the backend RPC OR's the flags on save — see docs/migrations/
   * consultas_administrativo_split_and_start_consultation_rls.sql). This mirrors
   * that here so the matrix never shows something that won't match after save:
   * an admin building a doctor role only has to check "Consultas"; the
   * Administrativo row visibly follows and is locked while it does.
   */
  isImpliedByConsultas(permission: any, action: PermissionAction): boolean {
    if (permission.name !== MODULE.CONSULTAS_ADMIN || action === 'can_view') return false;
    const consultas = this.availablePermissions.find((p) => p.name === MODULE.CONSULTAS);
    const consultasFlags = consultas && this.selectedPermissions[consultas.id];
    return !!consultasFlags?.[action];
  }

  isActionEnabled(permission: any, action: PermissionAction): boolean {
    return (
      this.isImpliedByConsultas(permission, action) ||
      !!this.selectedPermissions[permission.id]?.[action]
    );
  }

  /** Toggle a single cell (module × action). No-ops on a cell implied by Consultas —
   * that one can only be cleared by unchecking the matching Consultas cell. */
  toggleAction(permission: any, action: PermissionAction) {
    if (this.readOnly || this.isCellInert(permission, action)) return;
    const flags = this.flagsFor(permission, true)!;
    flags[action] = !flags[action];
    this.pruneIfEmpty(permission);
  }

  /** Whole row: true when every action for the module is enabled (including any
   * implied by Consultas). */
  isRowFull(permission: any): boolean {
    return this.permissionActions
      .filter((a) => this.hasAction(permission, a.key))
      .every((a) => this.isActionEnabled(permission, a.key));
  }

  /** Grant or clear every action for one module. */
  toggleRow(permission: any) {
    if (this.readOnly || this.isChildLocked(permission)) return;
    const enableAll = !this.isRowFull(permission);
    if (enableAll) {
      const flags = this.flagsFor(permission, true)!;
      for (const a of this.permissionActions) {
        if (this.hasAction(permission, a.key)) flags[a.key] = true;
      }
    } else {
      delete this.selectedPermissions[permission.id];
    }
  }

  /** Whole column: true when every visible module that draws this action has it enabled.
   * Child rows never count: the bulk grant skips them (they are locked at click time), so
   * counting them would show a half-empty header right after a select-all. */
  isColumnFull(action: PermissionAction): boolean {
    const modules = this.filteredPermissions.filter(
      (p) => this.hasAction(p, action) && !this.isChild(p),
    );
    return modules.length > 0 && modules.every((p) => this.isActionEnabled(p, action));
  }

  /** Grant or clear one action across every visible module. */
  toggleColumn(action: PermissionAction) {
    if (this.readOnly) return;
    const enableAll = !this.isColumnFull(action);
    // Locked BEFORE the loop: a child live when the header is clicked clears with its parent,
    // instead of becoming locked mid-loop by the parent's own change and being skipped.
    const locked = new Set(
      this.filteredPermissions.filter((p) => this.isChildLocked(p)).map((p) => p.id),
    );
    for (const p of this.filteredPermissions) {
      if (!this.hasAction(p, action) || locked.has(p.id)) continue;
      if (enableAll) {
        this.flagsFor(p, true)![action] = true;
      } else {
        const flags = this.selectedPermissions[p.id];
        if (flags) {
          flags[action] = false;
          this.pruneIfEmpty(p);
        }
      }
    }
  }

  /** Search-filtered module list shown in the matrix (matches the raw DB name and the precomputed translated label). */
  get filteredPermissions(): any[] {
    const term = this.permissionSearch.trim().toLowerCase();
    if (!term) return this.availablePermissions;
    return this.availablePermissions.filter((p) =>
      (p.name || '').toLowerCase().includes(term) || (p.search_label || '').includes(term)
    );
  }

  /** Count of modules granted at least one action. */
  get grantedCount(): number {
    return Object.keys(this.selectedPermissions).length;
  }

  /** True when this profile's matrix grants WRITE access to Consultas (a "doctor role"). */
  get grantsConsultasCreate(): boolean {
    const consultas = this.availablePermissions.find(
      (p) => (p.name || '').toLowerCase() === 'consultas',
    );
    const flags = consultas && this.selectedPermissions[consultas.id];
    return !!(flags && (flags.can_create || flags.can_edit || flags.can_delete));
  }

  async saveProfile() {
    if (this.readOnly) return;
    if (this.profileForm.invalid) {
      this.showError = true;
      return;
    }

    // A doctor role (Consultas:create) can only be assigned to Médico (type 3) users.
    if (this.grantsConsultasCreate) {
      const nonMedic = this.selectedUsers.filter(
        (u) => u.user_type_id != null && u.user_type_id !== 3,
      );
      if (nonMedic.length) {
        toast.error(this.translate.instant('settings_role_requires_medic'));
        return;
      }
    }

    this.showError = false;
    this.saving = true;

    const accessLevels: AccessLevelGrant[] = Object.entries(this.selectedPermissions).map(
      ([levelId, flags]) => ({
        level_id: Number(levelId),
        can_view: flags.can_view,
        can_create: flags.can_create,
        can_edit: flags.can_edit,
        can_delete: flags.can_delete,
      }),
    );

    const profileData = {
      name: this.profileForm.value.name,
      userIds: this.selectedUsers.map(u => u.user_id),
      accessLevels,
      branchId: this.branchId!,
      profileId: this.profileId
    };

    try {
      if (this.isEditMode && this.profileId) {
        await firstValueFrom(
          this.settingsService.updateAccessProfile(
            profileData.profileId!,
            profileData.name,
            profileData.accessLevels,
            profileData.userIds,
            profileData.branchId
          )
        );
        toast.success(this.translate.instant('settings_profile_success_update'));
      } else {
        await firstValueFrom(
          this.settingsService.createAccessProfile(
            profileData.name,
            profileData.accessLevels,
            profileData.userIds,
            profileData.branchId
          )
        );
        toast.success(this.translate.instant('settings_profile_success_create'));
      }
      
      this.saved.emit();
      this.close.emit();
    } catch (err) {
      console.error(err);
      const msg = (err as any)?.message || '';
      if (msg.includes('DOCTOR_ROLE_REQUIRES_MEDIC')) {
        toast.error(this.translate.instant('settings_role_requires_medic'));
      } else {
        toast.error(this.translate.instant(this.isEditMode ? 'settings_profile_error_update' : 'settings_profile_error_create'));
      }
    } finally {
      this.saving = false;
    }
  }
}
